import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { getPlanList } from "../../api/planList";
import { getPlanAccountList } from "../../api/planAccountList";
import { getGroupList } from "../../api/groupList";
import { getPlanBenefitList } from "../../api/planBenefitList";
import { useEffect, useMemo } from "react";
import { General } from "capitalrx-components-2.0";
import { useAuthToken } from "capitalrx-shell";

type options = Omit<UseQueryOptions, "queryKey">;
type axiosErrorType = AxiosError<any, any>;

type UseGetHook<Request, Response> = (
  request?: Request,
  options?: options,
  queryKey?: ReadonlyArray<string>,
) => UseQueryResult<Response, axiosErrorType>;

export function useCreateGetListHook<Request, Response>(
  url: string,
  apiCall: (request: Request, authToken: string) => Promise<Response>,
  defaultOptions?: Omit<UseQueryOptions<Response, AxiosError>, "queryKey">,
): UseGetHook<Request, Response> {
  const query = (request, options, queryKey) => {
    const [authToken] = useAuthToken();
    const showAlert = General.useAlert();
    const callOptions = { ...defaultOptions, ...options };

    const call: UseQueryResult<Response, axiosErrorType> = useQuery<Response, axiosErrorType>({
      queryKey: [url, request, queryKey],
      queryFn: async () => apiCall(request, authToken),
      ...callOptions,
    });

    const error = call.error as axiosErrorType;

    useEffect(() => {
      if (call?.isError) {
        showAlert({
          message: error?.response?.data?.message || error?.message,
          type: "error",
          style: "toast",
        });
      }
    }, [call, error]);

    return useMemo(() => call, [call]);
  };

  return query;
}

export const useGetPlanList = useCreateGetListHook("/plan/list", getPlanList, {
  placeholderData: {
    results: [],
    result_count: "0",
  },
  staleTime: 600000, // 10 minutes
});

export const useGetPlanAccountList = useCreateGetListHook("/plan_account/list", getPlanAccountList, {
  placeholderData: {
    results: [],
    result_count: "0",
  },
  staleTime: 600000, // 5 minutes
});

export const useGetGroupList = useCreateGetListHook("/group/list", getGroupList, {
  placeholderData: {
    results: [],
    result_count: "0",
  },
  staleTime: 600000, // 10 minutes
});

export const useGetPlanBenefitList = useCreateGetListHook("/plan_benefit/list", getPlanBenefitList, {
  placeholderData: {
    results: [],
    result_count: "0",
  },
  staleTime: 600000, // 10 minutes
});
